// Medya katmanı — **web ve varsayılan** uygulama (9.4.4.1).
//
// Tarayıcının kendi WebRTC'si kullanılıyor: web yüzeyi native build ve mağaza
// incelemesi olmadan yayınlanabildiği için burada ek bir bağımlılık yok.
// Mobilde Metro bunun yerine `media.native.ts`'i çözer.
//
// Sinyalleşme non-trickle (9.4.5.1): adaylar tek tek akıtılmıyor, toplama
// bitene kadar beklenip tek bir SDP gönderiliyor — böylece kurulum birkaç HTTP
// isteğine iniyor ve soket (10.1) gerekmiyor.
//
// Tipleme bilinçli olarak gevşek: projenin tsconfig'i React Native için kurulu
// ve DOM kütüphanesini içermiyor. Yalnızca bu dosya için DOM tiplerini açmak
// uygulamanın geri kalanında olmayan global'leri varmış gibi gösterirdi.
import {
  ICE_GATHER_TIMEOUT_MS,
  type CallMedia, type CallMediaOptions, type CallStreams,
  type IceServer, type MediaConnectionState,
} from './types';

export type { CallMedia, IceServer } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyPC = any;

const g: any = globalThis as any;
const PeerConnection: any = typeof g.RTCPeerConnection === 'function' ? g.RTCPeerConnection : undefined;

/**
 * Gerçek ses taşınabiliyor mu? Tarayıcıda WebRTC ya da mikrofon erişimi yoksa
 * (ör. jsdom, güvensiz köken, çok eski tarayıcı) `false` — arayüz bunu
 * kullanıcıya söylüyor, çünkü ücret kabul anında işlemeye başlıyor ve sessiz
 * bir çağrı için para almak kabul edilemez.
 */
export const mediaAvailable =
  PeerConnection !== undefined && typeof g.navigator?.mediaDevices?.getUserMedia === 'function';

// Toplama bitene kadar bekler; tavana takılırsa eldeki adaylarla devam eder.
async function waitForIce(pc: AnyPC): Promise<string> {
  if (pc.iceGatheringState !== 'complete') {
    await new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        pc.removeEventListener?.('icegatheringstatechange', onChange);
        resolve();
      };
      const onChange = () => { if (pc.iceGatheringState === 'complete') finish(); };
      const timer = setTimeout(finish, ICE_GATHER_TIMEOUT_MS);
      pc.addEventListener?.('icegatheringstatechange', onChange);
    });
  }
  return pc.localDescription?.sdp ?? '';
}

export function createCallMedia(iceServers: IceServer[], options: CallMediaOptions = {}): CallMedia {
  if (!PeerConnection) throw new Error('WebRTC bu ortamda yok');
  const pc: AnyPC = new PeerConnection({ iceServers });
  let local: AnyPC = null;
  let remote: AnyPC = null;
  let streamDinleyici: ((streams: CallStreams) => void) | null = null;
  const streamBildir = () => streamDinleyici?.({ local, remote });

  // D-855 — a browser does not play a received stream by itself, as the native
  // module does: without a media element the person on the web heard nothing.
  // The answer or call button was a click, and capture is running, so browsers
  // allow this element to play with sound.
  const speaker: AnyPC = typeof g.Audio === 'function' ? new g.Audio() : null;
  if (speaker) speaker.autoplay = true;
  const play = (stream: AnyPC) => {
    if (!speaker) return;
    speaker.srcObject = stream;
    speaker.play?.()?.catch?.(() => {
      // Refused anyway (a strict autoplay setting): the next tap anywhere retries.
      const retry = () => {
        g.document?.removeEventListener?.('pointerdown', retry);
        if (speaker.srcObject) speaker.play?.()?.catch?.(() => undefined);
      };
      g.document?.addEventListener?.('pointerdown', retry);
    });
  };

  // Uzak akış `track` olayıyla gelir; dinleyici kurulumdan ÖNCE bağlanmalı ki
  // olay düştüğünde kimse dinlemiyor olmasın.
  pc.addEventListener?.('track', (event: AnyPC) => {
    const gelen = event?.streams?.[0] ?? null;
    if (!gelen || gelen === remote) return;
    remote = gelen;
    play(remote);
    streamBildir();
  });

  // Akış teklif üretilmeden önce eklenmeli; aksi hâlde SDP medya taşımaz.
  const attachMedia = async () => {
    if (local) return;
    // Kamera yalnız görüntülü çağrıda istenir (9.4.14).
    local = await g.navigator.mediaDevices.getUserMedia({ audio: true, video: options.video === true });
    local.getTracks().forEach((track: AnyPC) => pc.addTrack(track, local));
    streamBildir();
  };

  return {
    async createOffer() {
      await attachMedia();
      await pc.setLocalDescription(await pc.createOffer());
      return waitForIce(pc);
    },
    async acceptOffer(offerSdp: string) {
      await pc.setRemoteDescription({ type: 'offer', sdp: offerSdp });
      await attachMedia();
      await pc.setLocalDescription(await pc.createAnswer());
      return waitForIce(pc);
    },
    async setAnswer(answerSdp: string) {
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
    },
    async restartIce() {
      // Aynı bağlantı üzerinde yeni ICE ufrag/pwd + taze adaylar (9.4.4.5); non-
      // trickle akışa uyum için toplama tamamlanana kadar beklenip tek SDP döner.
      await pc.setLocalDescription(await pc.createOffer({ iceRestart: true }));
      return waitForIce(pc);
    },
    setMuted(muted: boolean) {
      local?.getAudioTracks().forEach((track: AnyPC) => { track.enabled = !muted; });
    },
    setCameraEnabled(enabled: boolean) {
      local?.getVideoTracks?.().forEach((track: AnyPC) => { track.enabled = enabled; });
    },
    switchCamera() {
      // Tarayıcıda ön/arka geçişi aynı track üzerinde yapılamaz: yeni bir
      // `getUserMedia({ facingMode })` ve yeniden pazarlık gerekir. Web'in
      // görüntülü araması ayrı iş olduğu için burada bilinçli olarak boş.
    },
    onStreamChange(cb: (streams: CallStreams) => void) {
      streamDinleyici = cb;
      streamBildir();
    },
      onConnectionChange(cb: (state: MediaConnectionState) => void) {
        // `connectionstatechange` WebRTC'nin birleşik durumu; bazı yığınlarda
        // yalnız `iceconnectionstatechange` tetikleniyor, o yüzden ikisi de
        // dinleniyor ve aynı sadeleştirmeden geçiyor (9.4.4.5).
        const bildir = () => {
          const ham = pc.connectionState ?? pc.iceConnectionState;
          if (ham === 'connected' || ham === 'completed') cb('connected');
          else if (ham === 'disconnected') cb('interrupted');
          else if (ham === 'failed' || ham === 'closed') cb('failed');
          else cb('connecting');
        };
        pc.addEventListener?.('connectionstatechange', bildir);
        pc.addEventListener?.('iceconnectionstatechange', bildir);
      },
    close() {
      local?.getTracks().forEach((track: AnyPC) => track.stop());
      if (speaker) { speaker.pause?.(); speaker.srcObject = null; }
      local = null;
      remote = null;
      streamDinleyici = null;
      pc.close();
    },
  };
}
